import React, { useState } from 'react';

const AvailabilityManager = ({ availability, onUpdateAvailability }) => {
  const [formData, setFormData] = useState({
    status: availability?.status || 'normal',
    message: availability?.message || '',
    expectedReviewDate: availability?.expectedReviewDate || '',
    expirationDate: availability?.expirationDate || '',
    acceptHelpRequests: availability?.acceptHelpRequests !== false,
    acceptCampReports: availability?.acceptCampReports !== false,
    acceptVolunteerOffers: availability?.acceptVolunteerOffers !== false,
  });
  const [submitting, setSubmitting] = useState(false);

  // Sync if prop changes
  React.useEffect(() => {
    if (availability) {
      setFormData({
        status: availability.status || 'normal',
        message: availability.message || '',
        expectedReviewDate: availability.expectedReviewDate || '',
        expirationDate: availability.expirationDate || '',
        acceptHelpRequests: availability.acceptHelpRequests !== false,
        acceptCampReports: availability.acceptCampReports !== false,
        acceptVolunteerOffers: availability.acceptVolunteerOffers !== false,
      });
    }
  }, [availability]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await onUpdateAvailability(formData);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="chrome-panel border border-white/5 rounded-lg p-6 bg-charcoal-card/60 text-slate-200">
        <h2 className="text-xl font-bold text-white mb-2 uppercase tracking-tight font-display">Public Availability Status Banners</h2>
        <p className="text-xs text-slate-400 mb-6">
          Modify availability banners visible to public users on home page and public request forms.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4 max-w-xl">
          <div className="space-y-2 border-b border-white/5 pb-4 mb-4">
            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Intake Controls</h3>
            <div className="flex flex-col gap-2">
              <label className="flex items-center space-x-2 text-sm text-slate-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={formData.acceptHelpRequests}
                  onChange={(e) => setFormData({ ...formData, acceptHelpRequests: e.target.checked })}
                  className="rounded border-white/10 bg-charcoal-dark text-urgent-orange focus:ring-urgent-orange"
                />
                <span>Accept new Get Help requests</span>
              </label>
              <label className="flex items-center space-x-2 text-sm text-slate-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={formData.acceptCampReports}
                  onChange={(e) => setFormData({ ...formData, acceptCampReports: e.target.checked })}
                  className="rounded border-white/10 bg-charcoal-dark text-urgent-orange focus:ring-urgent-orange"
                />
                <span>Accept new Camp Reports</span>
              </label>
              <label className="flex items-center space-x-2 text-sm text-slate-300 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={formData.acceptVolunteerOffers}
                  onChange={(e) => setFormData({ ...formData, acceptVolunteerOffers: e.target.checked })}
                  className="rounded border-white/10 bg-charcoal-dark text-urgent-orange focus:ring-urgent-orange"
                />
                <span>Accept new Volunteer Offers</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Status Level</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm font-semibold text-white focus:ring-2 focus:ring-urgent-orange focus:border-transparent"
            >
              <option value="normal" className="bg-charcoal-card text-white">Accepting requests normally</option>
              <option value="delayed" className="bg-charcoal-card text-white">Delayed response expected</option>
              <option value="unavailable" className="bg-charcoal-card text-white">Temporarily unavailable / Closed</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Public Alert message</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows="3"
              className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-urgent-orange focus:border-transparent resize-none"
              placeholder="Enter additional details for public view..."
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Expected Review Date & Time</label>
              <input
                type="datetime-local"
                value={formData.expectedReviewDate}
                onChange={(e) => setFormData({ ...formData, expectedReviewDate: e.target.value })}
                className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-urgent-orange focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Expiration Date & Time</label>
              <input
                type="datetime-local"
                value={formData.expirationDate}
                onChange={(e) => setFormData({ ...formData, expirationDate: e.target.value })}
                className="w-full bg-charcoal-dark border border-white/10 rounded p-2 text-sm text-white focus:ring-2 focus:ring-urgent-orange focus:border-transparent"
              />
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="cyber-btn-orange py-2 px-6 text-xs uppercase font-bold"
            >
              {submitting ? 'Saving...' : 'Update Status Banner'}
            </button>
          </div>
        </form>
      </div>

      {/* Donation Handles warning check */}
      <div className="chrome-panel border border-white/5 rounded-lg p-6 bg-charcoal-card/60 text-slate-200">
        <h2 className="text-xl font-bold text-white mb-2 uppercase tracking-tight font-display">Donation Setup Warnings</h2>
        <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-200 p-4 rounded-lg text-sm mt-3 leading-relaxed">
          ⚠️ <strong>Donation Handle Review Checklist:</strong><br />
          The Cash App account link points to handle <strong>$ArtFiendStudios</strong>. This handle name differs from the nonprofit entity legal registration <strong>Not By Chance Inc</strong>. In client bundles, verify this has been authorized by administrators, or update handles to prevent donor confusion. Do not claim this account belongs directly to Not By Chance Inc. unless legal matching documentation is verified.
        </div>
      </div>
    </div>
  );
};

export default AvailabilityManager;
